import React, { useState, useMemo } from 'react';
import { Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BrandScorecardMetric } from '../../../../../types/scorecard';
import { ADOPTION_COLORS, PROGRESS_COLORS } from '../../../../constants/graphColors';
import PercentageProgress from './PercentageProgress';
import '../../../../../stylesheets/components/receiver/scorecard.scss';

interface BrandScorecardMetricsProps {
  data: BrandScorecardMetric[];
}

const BrandScorecardMetrics: React.FC<BrandScorecardMetricsProps> = ({ data }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const getValidationProgressColor = (percentage: number) => {
    if (percentage >= 90) return PROGRESS_COLORS.LIVE;
    if (percentage >= 70) return PROGRESS_COLORS.SUBMITTED;
    return PROGRESS_COLORS.IN_PREPARATION;
  };

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchValue.trim()) return data;

    const searchLower = searchValue.toLowerCase().trim();
    return data.filter(item => item.brand.toLowerCase().includes(searchLower));
  }, [data, searchValue]);

  const columns = [
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 120,
      sorter: (a: BrandScorecardMetric, b: BrandScorecardMetric) => a.brand.localeCompare(b.brand),
      render: (text: string) => (
        <div className="text-xs font-medium" title={text}>
          {text}
        </div>
      ),
    },
    {
      title: 'SKUs',
      dataIndex: 'skus',
      key: 'skus',
      width: 80,
      align: 'center' as const,
      sorter: (a: BrandScorecardMetric, b: BrandScorecardMetric) => a.skus - b.skus,
      render: (value: number) => <div className="text-xs">{value.toLocaleString()}</div>,
    },
    {
      title: 'SKUs with Validation Fulfilled',
      dataIndex: 'skusWithValidation',
      key: 'skusWithValidation',
      width: 120,
      align: 'center' as const,
      sorter: (a: BrandScorecardMetric, b: BrandScorecardMetric) =>
        a.skusWithValidation - b.skusWithValidation,
      render: (value: number) => <div className="text-xs">{value.toLocaleString()}</div>,
    },
    {
      title: '% SKUs with Validation Fulfilled',
      dataIndex: 'percentageWithValidation',
      key: 'percentageWithValidation',
      width: 140,
      sorter: (a: BrandScorecardMetric, b: BrandScorecardMetric) =>
        a.percentageWithValidation - b.percentageWithValidation,
      render: (percentage: number) => (
        <PercentageProgress percentage={percentage} strokeColor={PROGRESS_COLORS.READY} />
      ),
    },
    {
      title: '% Validations Fulfilled',
      dataIndex: 'percentageValidationsFulfilled',
      key: 'percentageValidationsFulfilled',
      width: 140,
      sorter: (a: BrandScorecardMetric, b: BrandScorecardMetric) =>
        a.percentageValidationsFulfilled - b.percentageValidationsFulfilled,
      render: (percentage: number) => (
        <PercentageProgress
          percentage={percentage}
          strokeColor={getValidationProgressColor(percentage)}
        />
      ),
    },
  ];

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 text-center scorecard-no-data">
        {t('receiver:noBrandData')}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <Input
          placeholder={t('receiver:searchByBrand')}
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          size="small"
          allowClear
        />
      </div>
      <Table
        dataSource={filteredData}
        columns={columns}
        pagination={false}
        size="small"
        rowKey="brand"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: searchValue.trim()
            ? t('receiver:noMatchingBrands')
            : t('receiver:noBrandData'),
        }}
      />
    </div>
  );
};

export default BrandScorecardMetrics;
